import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { PLATFORM_ID, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthSessionService } from '../services/session/auth-session.service';

const authEndpointPaths = ['/auth/obtain-tokens', '/auth/refresh-tokens'];
const apiBaseUrl = environment.apiUrl.replace(/\/+$/, '');

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  if (!isBrowser || !isApiRequest(req.url) || isAuthRequest(req.url)) {
    return next(req);
  }

  const router = inject(Router);
  const session = inject(AuthSessionService);
  const accessToken = session.getAccessToken();
  const refreshToken = session.getRefreshToken();

  if (!accessToken && refreshToken) {
    return session.refreshTokens().pipe(
      switchMap((tokens) => next(addAuthorizationHeader(req, tokens.access_token))),
      catchError((error: unknown) => handleAuthFailure(error, session, router)),
    );
  }

  if (!accessToken) {
    return next(req).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return handleAuthFailure(error, session, router);
        }

        return throwError(() => error);
      }),
    );
  }

  if (refreshToken && session.shouldRefreshAccessToken()) {
    return session.refreshTokens().pipe(
      switchMap((tokens) => next(addAuthorizationHeader(req, tokens.access_token))),
      catchError((error: unknown) => handleAuthFailure(error, session, router)),
    );
  }

  return next(addAuthorizationHeader(req, accessToken)).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      if (!session.getRefreshToken()) {
        return handleAuthFailure(error, session, router);
      }

      return session.refreshTokens().pipe(
        switchMap((tokens) => next(addAuthorizationHeader(req, tokens.access_token))),
        catchError((refreshError: unknown) => handleAuthFailure(refreshError, session, router)),
      );
    }),
  );
};

function addAuthorizationHeader(
  request: HttpRequest<unknown>,
  token: string,
): HttpRequest<unknown> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}

function handleAuthFailure(
  error: unknown,
  session: AuthSessionService,
  router: Router,
) {
  session.clear();
  void router.navigateByUrl('/staff/auth');

  return throwError(() => error);
}

function isAuthRequest(url: string): boolean {
  return authEndpointPaths.some((path) => url.includes(path));
}

function isApiRequest(url: string): boolean {
  if (!apiBaseUrl) {
    return false;
  }

  return url === apiBaseUrl || url.startsWith(`${apiBaseUrl}/`);
}
